import axios from 'axios';

// Silent access-token refresh: when an authenticated API call returns 401 because
// the short-lived access cookie expired, transparently POST /api/auth/refresh once
// (single-flight) using the 7-day refresh cookie, then retry the original request.
// Active users never see an interruption. Only when the refresh ALSO fails (refresh
// token expired/invalid) do we surface "session expired" — and only if the user was
// actually logged in (so an anonymous first-load /me check never triggers it).

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Endpoints that must NOT trigger the refresh/logout flow (avoid loops + wrong-password 401s).
const AUTH_EXEMPT = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
];

let refreshPromise = null;
let installed = false;
let opts = { isLoggedIn: () => false, onSessionExpired: () => {} };

const isApiUrl = (url) => typeof url === 'string' && url.includes('/api/');
const isExempt = (url) => typeof url === 'string' && AUTH_EXEMPT.some((p) => url.includes(p));

// Single-flight: concurrent 401s share one in-flight refresh call.
function doRefresh() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        return r.ok;
      } catch {
        return false;
      }
    })();
    refreshPromise.finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

function handleExpired() {
  // Only bounce a genuinely logged-in user; never on an anonymous first load.
  if (opts.isLoggedIn()) {
    opts.onSessionExpired();
  }
}

export function installAuthRefresh(options) {
  opts = { ...opts, ...options };
  if (installed) return;
  installed = true;

  // ---- axios (shared singleton across all axios imports) ----
  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const cfg = error.config || {};
      const status = error.response && error.response.status;
      const url = cfg.url || '';
      if (status === 401 && !cfg._authRetried && isApiUrl(url) && !isExempt(url)) {
        cfg._authRetried = true;
        const ok = await doRefresh();
        if (ok) return axios(cfg);
        handleExpired();
      }
      return Promise.reject(error);
    }
  );

  // ---- fetch (wrap once; only touches our /api/* calls, passes everything else through) ----
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const resp = await originalFetch(input, init);
    if (
      resp.status === 401 &&
      isApiUrl(url) &&
      !isExempt(url) &&
      !(init && init._authRetried)
    ) {
      const ok = await doRefresh();
      if (ok) {
        return originalFetch(input, { ...(init || {}), _authRetried: true });
      }
      handleExpired();
    }
    return resp;
  };
}
